<template>
	<div class="location-breadcrumbs">
		<GlobalBreadcrumbs :items="breadcrumbsItems" />
	</div>
</template>

<script setup lang="ts">
const { locale, t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()

const cmsLocation = inject<Ref<ICMSLocationModel | undefined>>('cmsLocation') || ref<ICMSLocationModel | undefined>(undefined)
const sameCityLocations = inject<Ref<ICMSLocationModel[]>>('sameCityLocations') || ref<ICMSLocationModel[]>([])

const getURLforLocation = (item: ICMSLocationModel) => {
	const urlLocale = item[`isActive${locale.value.toUpperCase()}`] ? item[`url${locale.value.toUpperCase()}`] : undefined
	const url = item.urlPL

	if (!url && !urlLocale) return undefined
	else
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
}

const BREADCRUMBS_KEY = 'modules.location_module.location.breadcrumbs'

const getLocationName = (location: ICMSLocationModel) => location[`name${locale.value.toUpperCase()}`] ?? location.namePL

const getLocationTitle = (location: ICMSLocationModel) => {
	const city = getLocationName(location)

	if (location.deliveryBus) return t(`${BREADCRUMBS_KEY}.delivery_bus`, { city })
	if (location.passengerBus) return t(`${BREADCRUMBS_KEY}.passenger_bus`, { city })
	if (location.icon === 'airplane') return city
	return t(`${BREADCRUMBS_KEY}.car_rental`, { city })
}

// once every page has a mainCmsLocationId, that anchor is the natural middle level;
// skip it while uncategorized, or when the current page is the anchor itself
const breadcrumbsItems = computed(() => {
	const items = [{ title: t('common.home_page'), href: localePath('/') }]

	if (!cmsLocation.value) return items

	const isOwnAnchor = !cmsLocation.value.mainCmsLocationId || cmsLocation.value.mainCmsLocationId === cmsLocation.value.id

	if (!isOwnAnchor) {
		const anchorLocation = sameCityLocations.value.find((location) => location.id === cmsLocation.value?.mainCmsLocationId)
		const anchorHref = anchorLocation ? getURLforLocation(anchorLocation) : undefined

		if (anchorLocation && anchorHref) {
			items.push({ title: t(`${BREADCRUMBS_KEY}.car_rental`, { city: getLocationName(anchorLocation) }), href: anchorHref })
		}
	}

	items.push({ title: getLocationTitle(cmsLocation.value), href: route.fullPath })

	return items
})

useBreadcrumbJsonld(breadcrumbsItems)
</script>

<style lang="scss" scoped src="./Breadcrumbs.scss"></style>
